import TextButton from 'LuxKit/Button/TextButton'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import ContentfulFormField from './ContentfulFormField'
import Group from 'components/utils/Group'
import styled from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'
import { getThemeClassName } from 'lib/theme/themeUtils'
import BodyText from 'LuxKit/Typography/BodyText'
import { formToObject } from 'lib/forms/formToObject'
import { sendContentfulFormData } from 'api/promo'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { useAppSelector } from 'hooks/reduxHooks'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { ContentfulVideoBlock } from './ContentfulVideoBlock'
import ModalContext from 'contexts/ModalContext'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import ContentfulImageButtonGroup from './ContentfulImageButtonGroup'
import LayoutContainer from 'components/Common/LayoutContainer'

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  background-color: ${props => props.theme.palette.neutral.default.eight};

  &.background-color-white {
    background-color: ${props => props.theme.palette.neutral.default.eight};
  }

  &.background-color-grey {
    background-color: ${props => props.theme.palette.neutral.default.seven};
  }
`

const CentredGroup = styled(Group)`
  margin: ${rem(16)};

  ${mediaQueryUp.tablet} {
    max-width: ${rem(500)};
  }


  &.background-color-white {
    background-color: ${props => props.theme.palette.neutral.default.eight};
  }

  &.background-color-grey {
    background-color: ${props => props.theme.palette.neutral.default.seven};
  }
`

interface Props extends Omit<App.ComponentForm, 'contentTypeId'> {
  className?: string
}

function ContentfulForm(props: Props) {
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const { title, subtitle, slug, buttonText, successMessage, components, backgroundColor, loginRequired, loginRequiredButtonText, postSubmissionState, gridComponents } = props
  const isLoggedIn = useAppSelector(state => selectLoggedIn(state))
  const showModal = useContext(ModalContext)
  const { resolve } = useModalElementContext()
  const formRef = useRef<HTMLFormElement>(null)

  const loginHandler = useCallback((e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    showModal(<AccountAccessModal />)
  }, [showModal])

  useEffect(() => {
    formRef.current?.reset()
  }, [])

  async function handleFormSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const currentForm = e.currentTarget
    const data = formToObject<object>(currentForm)

    try {
      await sendContentfulFormData({ slug, data })
      currentForm.reset()
      setErrorMessage(null)
      setIsSubmitted(true)
    } catch(error) {
      console.error(error)
      setErrorMessage('Failed to send the form. Please try again.')
      setIsSubmitted(false)
    }
    if (!hasPostSubmissionState) {
      resolve()
    }
  }

  const showSubmitButton = useMemo(() => !loginRequired || (loginRequired && isLoggedIn), [
    loginRequired,
    isLoggedIn,
  ])
  const hasPostSubmissionState = useMemo(() => !!postSubmissionState, [postSubmissionState])

  if (hasPostSubmissionState && isSubmitted) {
    return <ContentfulVideoBlock {...postSubmissionState as any} />
  }

  return (
    <form onSubmit={showSubmitButton ? handleFormSubmit : loginHandler} ref={formRef}>
      <FormContainer className={clsx(props.className, `background-color-${backgroundColor}`, 'size-small', getThemeClassName({ mobile: backgroundColor === 'dark' ? 'inverse' : 'default' }))}>
        <CentredGroup direction="vertical" id="centered-group" gap={8} className={clsx(`background-color-${backgroundColor}`)}>
          <HeadingTextBlock variant="heading2" colour="neutral-two">{title}</HeadingTextBlock>
          <BodyText variant="large" colour="neutral-two">{subtitle}</BodyText>
          {components?.map((component, index) => (
            <ContentfulFormField key={index} {...component} />
          ))}
        </CentredGroup>
        <LayoutContainer>
          {gridComponents?.length > 0 && <ContentfulImageButtonGroup gridComponents={gridComponents} name="image-button-group" />}
        </LayoutContainer>
        <CentredGroup direction="vertical" id="centered-group-2" gap={8}>
          <TextButton
            kind="primary"
            type="submit"
            disabled={isSubmitted}
          >
            {!showSubmitButton && loginRequiredButtonText}
            {showSubmitButton && isSubmitted && 'Sent successfully'}
            {showSubmitButton && !isSubmitted && buttonText}
          </TextButton>
          {errorMessage && <BodyTextBlock variant="medium" colour="critical">{errorMessage}</BodyTextBlock>}
          {isSubmitted && <BodyTextBlock variant="medium" colour="neutral-two">{successMessage}</BodyTextBlock>}
        </CentredGroup>
      </FormContainer>
    </form>
  )
}

export default ContentfulForm
